import { useContext, useEffect, useState } from "react";
import IVehicle from "../../../../models/IVehicle";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import { buildRestUrl, buildAuthHeadersForFetch } from "../../../../services/RestService";
import RestEndpointsEnum from "../../../../models/RestEndpointsEnum";
import INotification from "../../../../models/INotification";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import { useAppContextNonNull } from "@contexts/AppContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import IRoute from "../../../../models/IRoute";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IGetRoutesDTO from "../../../../models/DTO/IGetRoutesDTO";
import IOnGetRoutesDTO from "../../../../models/DTO/IOnGetRoutesDTO";
import IWarehouse from "../../../../models/IWarehouse";
import IGetWarehousesDTO from "../../../../models/DTO/IGetWarehousesDTO";
import IOnGetWarehousesDTO from "../../../../models/DTO/IOnGetWarehousesDTO";

type Props = {
    cancelFn: (refresh?: boolean) => void;
    vehicle?: IVehicle;
};

export const VehicleSidebar = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const socket = useContext(SocketContext)!;

    const isEditMode = !!props.vehicle;

    const [name, setName] = useState<string>(props.vehicle?.name || "");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [routes, setRoutes] = useState<IRoute[]>([]);
    const [defaultRouteId, setDefaultRouteId] = useState<number | undefined>(props.vehicle?.default_route_id);
    // Van-sell/DSD depot: the warehouse this vehicle loads from and unloads to.
    // Vans themselves are never depots — the list is fetched WITHOUT includeVehicles.
    const [warehouses, setWarehouses] = useState<IWarehouse[]>([]);
    const [defaultWarehouseId, setDefaultWarehouseId] = useState<number | undefined>(props.vehicle?.default_warehouse_id ?? undefined);
    const showDepotPicker = !!supplierModel.has_truck_inventory;

    const getRoutes = () => {
        const payload: IGetRoutesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };
        socket.emit(EndpointsEnum.RoutesGetRoutes, payload);
    };

    const onGetRoutes = (data: IOnGetRoutesDTO) => {
        setRoutes(data.routes);
    };

    const getWarehouses = () => {
        const payload: IGetWarehousesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };
        socket.emit(EndpointsEnum.WarehouseGetWarehouses, payload);
    };

    const onGetWarehouses = (data: IOnGetWarehousesDTO) => {
        setWarehouses(data.warehouses);
    };

    useEffect(() => {
        socket.on(EndpointsEnum.RoutesOnGetRoutes, onGetRoutes);
        getRoutes();
        if (showDepotPicker) {
            socket.on(EndpointsEnum.WarehouseOnGetWarehouses, onGetWarehouses);
            getWarehouses();
        }
        return () => {
            socket.removeListener(EndpointsEnum.RoutesOnGetRoutes, onGetRoutes);
            socket.removeListener(EndpointsEnum.WarehouseOnGetWarehouses, onGetWarehouses);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cancel = () => {
        props.cancelFn();
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim() || isSubmitting) return;

        setIsSubmitting(true);

        try {
            let url: string;
            let method: string;

            if (isEditMode) {
                url = buildRestUrl({
                    endpoint: RestEndpointsEnum.UpdateVehicle,
                    params: { vehicleGuid: props.vehicle!.guid },
                });
                method = "PUT";
            } else {
                url = buildRestUrl({ endpoint: RestEndpointsEnum.CreateVehicle });
                method = "POST";
            }

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
            });

            const response = await fetch(url, {
                method,
                headers,
                body: JSON.stringify({
                    name: name.trim(),
                    default_route_id: defaultRouteId || null,
                    ...(showDepotPicker ? { default_warehouse_id: defaultWarehouseId || null } : {}),
                }),
            });

            if (response.ok) {
                const notification: INotification = {
                    position: NotificationPositionEnum.bc,
                    level: NotificationLevelEnum.success,
                    message: isEditMode ? "vehicle_updated_successfully" : "vehicle_created_successfully",
                };

                uiContext.notifyFn(null, notification);
                props.cancelFn(true);
            } else {
                const notification: INotification = {
                    position: NotificationPositionEnum.bc,
                    level: NotificationLevelEnum.error,
                    message: isEditMode ? "vehicle_update_failed" : "vehicle_create_failed",
                };

                uiContext.notifyFn(null, notification);
            }
        } catch (error) {
            console.error("Error saving vehicle:", error);
            const notification: INotification = {
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.error,
                message: "error_occurred",
            };

            uiContext.notifyFn(null, notification);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={"VehicleSidebar p-2"}>
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-cars"} title={isEditMode ? "edit_vehicle" : "create_vehicle"} />
                    <hr />
                </div>
            </div>

            <div className="row">
                <form onSubmit={handleSave}>
                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"name"} isBold={true} />
                        </div>
                        <div>
                            <OwInputWrapper
                                name="name"
                                value={name}
                                fn={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                                isRequired={true}
                                autoFocus={true}
                            />
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"default_route"} isBold={true} />
                        </div>
                        <div>
                            <OwSelectOption
                                name="default_route_id"
                                options={routes}
                                selectedOption={{ default_route_id: defaultRouteId }}
                                selectedOptionProp="default_route_id"
                                valueProp="id"
                                titleProp="name"
                                fn={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    const value = e.target.value;
                                    setDefaultRouteId(value ? Number(value) : undefined);
                                }}
                                showBlankFirst={true}
                            />
                        </div>
                    </div>

                    {showDepotPicker && (
                        <div className={"d-flex flex-column mb-4"}>
                            <div>
                                <OwLabel title={"depot_warehouse"} isBold={true} />
                            </div>
                            <div>
                                <OwSelectOption
                                    name="default_warehouse_id"
                                    options={warehouses}
                                    selectedOption={{ default_warehouse_id: defaultWarehouseId }}
                                    selectedOptionProp="default_warehouse_id"
                                    valueProp="id"
                                    titleProp="name"
                                    fn={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        const value = e.target.value;
                                        setDefaultWarehouseId(value ? Number(value) : undefined);
                                    }}
                                    showBlankFirst={true}
                                />
                            </div>
                            <small className="text-muted mt-1">
                                <OwLabel title={"depot_warehouse_help"} />
                            </small>
                        </div>
                    )}

                    <div className={"d-flex"}>
                        <OwButton type={"submit"} title={"save"} active={isSubmitting} disableOnActive={true} />
                        <OwButton title={"cancel"} className={"ms-2 btn-secondary"} fn={cancel} />
                    </div>
                </form>
            </div>
        </div>
    );
};
